import {
  Card,
  Classes,
  H5,
  H6,
  Icon,
  InputGroup,
  Intent,
  MaybeElement,
} from '@blueprintjs/core';
import { IconName, IconNames } from '@blueprintjs/icons';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { Dialog, Text, Tooltip, VoiceboxLogo } from 'vet-bones/components';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { TEST_IDS } from 'src/constants/testIds';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { generateDateTimeId } from 'src/utils/generateId';
import { useNavigateProject } from 'src/utils/hooks/useNavigateProject';
import { usePortalFeatures } from 'src/utils/hooks/usePortalFeatures';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

enum CreateProjectDialogType {
  VOICEBOX = 'voicebox',
  BLANK_CANVAS = 'blank-canvas',
  IMPORT_MODEL = 'import-model',
  CONNECT_RESOURCE = 'connect-resource',
}

type NewProjectCardProps = {
  title: string;
  description: string;
  icon: IconName | MaybeElement;
  onClick: (e: any) => void;
  testId: string;
};

const NewProjectCard: React.VFC<NewProjectCardProps> = ({
  title,
  description,
  icon,
  onClick,
  testId,
}) => {
  return (
    <div>
      <H5>{title}</H5>
      <Card data-testid={testId} interactive onClick={onClick}>
        {typeof icon === 'string' ? <Icon icon={icon} size={60} /> : icon}
        <Text className={Classes.TEXT_SMALL}>{description}</Text>
      </Card>
    </div>
  );
};

export const CreateProjectDialog: React.VFC = () => {
  const [projectTitle, setProjectTitle] = React.useState('');
  const [hasTyped, setHasTyped] = React.useState(false);

  const dispatch = useReduxDispatch();
  const navigateProject = useNavigateProject();
  const portalFeatures = usePortalFeatures();
  const isVoiceboxButtonVisible = portalFeatures?.data?.voiceBox;

  const isOpen = useReduxSelector(
    (state) => state.ui.isCreateProjectDialogOpen
  );

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledCreateProjectDialog({ isOpen: false }));
  }, [dispatch]);

  const handleSubmit = React.useCallback(
    (projectAction: CreateProjectDialogType) => {
      if (!projectTitle) {
        setHasTyped(true);
        return;
      }
      const id = generateDateTimeId();
      const dateCreated = Date.now();
      const openProjectModelDialog = () => {
        dispatch(
          uiSlice.actions.toggledProjectModelDialog({ isPendingOpen: true })
        );
      };
      const openProjectResourceDialog = () => {
        dispatch(
          uiSlice.actions.toggledProjectResourceDialog({ isPendingOpen: true })
        );
      };
      const openVoiceboxDialog = () => {
        dispatch(
          uiSlice.actions.toggledVoiceboxDialog({ isPendingOpen: true })
        );
      };
      dispatch(
        projectsActionCreators.addedProject({
          id,
          name: projectTitle,
          dateCreated,
        })
      );

      handleClose();
      navigateProject(id);
      sendHubspotEvent(TrackingEventList.DESIGNER_CREATE_PROJECT);
      switch (projectAction) {
        case CreateProjectDialogType.BLANK_CANVAS:
          break;
        case CreateProjectDialogType.VOICEBOX:
          openVoiceboxDialog();
          break;
        case CreateProjectDialogType.IMPORT_MODEL:
          openProjectModelDialog();
          break;
        case CreateProjectDialogType.CONNECT_RESOURCE:
          openProjectResourceDialog();
          break;
        default:
          break;
      }
    },
    [dispatch, handleClose, navigateProject, projectTitle]
  );

  const errorTextForName = (name: string) => {
    return name ? '' : copy.components.createProjectDialog.validation.name;
  };
  const errorText = hasTyped ? errorTextForName(projectTitle) : '';

  React.useEffect(() => {
    if (!isOpen) {
      setProjectTitle('');
      setHasTyped(false);
    }
  }, [isOpen, setProjectTitle, setHasTyped]);

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-dialog-create-project"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      title={copy.components.createProjectDialog.title}
    >
      <div className={Classes.DIALOG_BODY}>
        <label className={Classes.LABEL} htmlFor="sd-create-project-value">
          <H6>{copy.components.createProjectDialog.name}</H6>
          <Tooltip
            content={errorText || ''}
            disabled={!errorText}
            fill
            intent={Intent.DANGER}
          >
            <InputGroup
              autoComplete="off"
              autoFocus
              data-testid={TEST_IDS.dialog.prompt.valueField}
              fill
              id="sd-create-project-value"
              intent={errorText ? Intent.DANGER : Intent.NONE}
              name="value"
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                setHasTyped(true);
                setProjectTitle(evt.target.value);
              }}
              placeholder={copy.components.createProjectDialog.namePlaceholder}
              value={projectTitle}
            />
          </Tooltip>
        </label>

        <H6>{copy.components.createProjectDialog.select}</H6>
        <div className="sd-dialog-create-project__card-container">
          {isVoiceboxButtonVisible ? (
            <NewProjectCard
              description={
                copy.components.createProjectDialog.voiceboxDescription
              }
              icon={<VoiceboxLogo size={60} />}
              onClick={() => handleSubmit(CreateProjectDialogType.VOICEBOX)}
              testId={TEST_IDS.dialog.newProject.voicebox}
              title={copy.components.createProjectDialog.voiceboxTitle}
            />
          ) : null}
          <NewProjectCard
            description={copy.components.createProjectDialog.blankDescription}
            icon={IconNames.SHAPES}
            onClick={() => handleSubmit(CreateProjectDialogType.BLANK_CANVAS)}
            testId={TEST_IDS.dialog.newProject.blankProject}
            title={copy.components.createProjectDialog.blankTitle}
          />
          <NewProjectCard
            description={copy.components.createProjectDialog.importDescription}
            icon={IconNames.DIAGRAM_TREE}
            onClick={() => handleSubmit(CreateProjectDialogType.IMPORT_MODEL)}
            testId={TEST_IDS.dialog.newProject.importModel}
            title={copy.components.createProjectDialog.importTitle}
          />
          <NewProjectCard
            description={
              copy.components.createProjectDialog.resourceDescription
            }
            icon={IconNames.DATA_CONNECTION}
            onClick={() =>
              handleSubmit(CreateProjectDialogType.CONNECT_RESOURCE)
            }
            testId={TEST_IDS.dialog.newProject.connectResource}
            title={copy.components.createProjectDialog.resourceTitle}
          />
        </div>
      </div>
    </Dialog>
  );
};
